import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, WarningCircle, CheckCircle, Plus, ArrowClockwise } from "@phosphor-icons/react";

const InventoryPage = () => {
  const [inventory, setInventory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restockDialog, setRestockDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [restockQty, setRestockQty] = useState("");
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ stock_quantity: "", low_stock_threshold: "", unit: "servings" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [invRes, alertsRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/alerts`)
      ]);
      setInventory(invRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error("Failed to load inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestock = async () => {
    if (!restockQty || !selectedItem) return;
    try {
      const res = await axios.post(`${API}/inventory/${selectedItem.id}/restock?quantity=${parseInt(restockQty)}`);
      toast.success(`${res.data.name}: +${res.data.added} ${selectedItem.unit || "servings"} (now ${res.data.new_stock})`);
      setRestockDialog(false);
      setRestockQty("");
      fetchData();
    } catch (error) {
      toast.error("Failed to restock");
    }
  };

  const handleUpdateInventory = async () => {
    if (!selectedItem) return;
    try {
      const data = {};
      if (editForm.stock_quantity !== "") data.stock_quantity = parseInt(editForm.stock_quantity);
      if (editForm.low_stock_threshold !== "") data.low_stock_threshold = parseInt(editForm.low_stock_threshold);
      if (editForm.unit) data.unit = editForm.unit;
      
      await axios.put(`${API}/menu/${selectedItem.id}/inventory`, data);
      toast.success("Inventory updated");
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const outOfStock = alerts.filter(a => a.type === "out_of_stock").length;
  const lowStock = alerts.filter(a => a.type === "low_stock").length;
  const tracked = inventory.filter(i => i.stock_quantity !== -1).length;

  return (
    <div className="min-h-screen bg-background" data-testid="inventory-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Inventory</h1>
            <p className="text-muted-foreground">Track stock levels and manage supplies</p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2" data-testid="refresh-inventory">
            <ArrowClockwise size={18} /> Refresh
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center"><Package size={24} className="text-primary" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Total Items</p><p className="font-mono text-2xl font-medium">{inventory.length}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><CheckCircle size={24} className="text-blue-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Tracked Items</p><p className="font-mono text-2xl font-medium">{tracked}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center"><WarningCircle size={24} className="text-yellow-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Low Stock</p><p className="font-mono text-2xl font-medium">{lowStock}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center"><WarningCircle size={24} className="text-red-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Out of Stock</p><p className="font-mono text-2xl font-medium">{outOfStock}</p></div>
          </CardContent></Card>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50/50">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg text-yellow-800">Inventory Alerts</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.map(alert => (
                  <div key={alert.id} className={`flex items-center justify-between p-3 rounded-lg ${alert.type === "out_of_stock" ? "bg-red-50 border border-red-200" : "bg-yellow-50 border border-yellow-200"}`} data-testid={`alert-${alert.id}`}>
                    <div className="flex items-center gap-3">
                      <WarningCircle size={20} weight="fill" className={alert.type === "out_of_stock" ? "text-red-600" : "text-yellow-600"} />
                      <span className="font-medium text-sm">{alert.name}</span>
                      <Badge variant="outline" className={alert.type === "out_of_stock" ? "text-red-700 border-red-300" : "text-yellow-700 border-yellow-300"}>
                        {alert.type === "out_of_stock" ? "OUT OF STOCK" : `Low: ${alert.stock} left`}
                      </Badge>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedItem(alert); setRestockDialog(true); }} data-testid={`restock-alert-${alert.id}`}>
                      <Plus size={14} className="mr-1" /> Restock
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inventory List */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">All Items</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
            ) : (
              <div className="space-y-2">
                {inventory.map(item => (
                  <div key={item.id} className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${item.is_out_of_stock ? "bg-red-50/50 border-red-200" : item.is_low_stock ? "bg-yellow-50/50 border-yellow-200" : "bg-secondary/30 border-border hover:border-primary/20"}`} data-testid={`inv-item-${item.id}`}>
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground capitalize">{item.category}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-medium">
                          {item.stock_quantity === -1 ? (
                            <span className="text-muted-foreground">Unlimited</span>
                          ) : (
                            <span className={item.is_out_of_stock ? "text-red-600" : item.is_low_stock ? "text-yellow-600" : ""}>
                              {item.stock_quantity} {item.unit}
                            </span>
                          )}
                        </p>
                        {item.stock_quantity !== -1 && (
                          <p className="text-xs text-muted-foreground">Alert at {item.low_stock_threshold}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={!item.available ? "text-red-600 border-red-300" : "text-green-600 border-green-300"}>
                        {item.available ? "Available" : "Disabled"}
                      </Badge>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedItem(item); setEditForm({ stock_quantity: item.stock_quantity === -1 ? "" : String(item.stock_quantity), low_stock_threshold: String(item.low_stock_threshold), unit: item.unit }); setEditDialog(true); }} data-testid={`edit-inv-${item.id}`}>
                          Set Stock
                        </Button>
                        {item.stock_quantity !== -1 && (
                          <Button size="sm" variant="outline" onClick={() => { setSelectedItem(item); setRestockDialog(true); }} data-testid={`restock-${item.id}`}>
                            <Plus size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Restock Dialog */}
      <Dialog open={restockDialog} onOpenChange={setRestockDialog}>
        <DialogContent data-testid="restock-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Restock: {selectedItem?.name}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Quantity to Add</Label>
              <Input type="number" min="1" placeholder="e.g. 50" value={restockQty} onChange={(e) => setRestockQty(e.target.value)} data-testid="restock-qty" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestockDialog(false)}>Cancel</Button>
            <Button onClick={handleRestock} data-testid="confirm-restock-btn">Restock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Inventory Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent data-testid="edit-inv-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Set Stock: {selectedItem?.name}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Stock Quantity (-1 for unlimited)</Label>
              <Input type="number" placeholder="-1" value={editForm.stock_quantity} onChange={(e) => setEditForm(f => ({ ...f, stock_quantity: e.target.value }))} data-testid="edit-stock-qty" />
            </div>
            <div className="space-y-2">
              <Label>Low Stock Alert Threshold</Label>
              <Input type="number" min="1" placeholder="10" value={editForm.low_stock_threshold} onChange={(e) => setEditForm(f => ({ ...f, low_stock_threshold: e.target.value }))} data-testid="edit-threshold" />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Input placeholder="servings" value={editForm.unit} onChange={(e) => setEditForm(f => ({ ...f, unit: e.target.value }))} data-testid="edit-unit" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateInventory} data-testid="save-inv-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
